import type * as THREE from 'three'
import {
  resolveImportedModelTopologyRepository,
} from '../../topology/ImportedModelTopologyRepository'
import type {
  ImportedModelTopologyRepository,
} from '../../topology/ImportedModelTopologyRepository'
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit'

/**
 * 管理实例 source Mesh 脱离物理父链后的拓扑仓库反查锚点。
 *
 * 装配体加载实例组与阵列独立实例组共享同一条拓扑反查规则。本注册表集中保证锚点注册和
 * 解除严格成对，避免各实例化领域分别维护 WeakMap 生命周期而产生悬空引用。
 */
export class ImportedModelTopologySourceLookupAnchorRegistry {
  /** 已注册 source Mesh 到原拓扑仓库的精确索引。 */
  private readonly repositoryBySourceMesh = new Map<THREE.Mesh, ImportedModelTopologyRepository>()

  /**
   * 注册一组实例 Unit 中即将脱离父链的 source Mesh。
   *
   * @param units 已完成 Registry 发布、尚未抑制 source Mesh 的实例 Unit。
   */
  public registerUnits(units: readonly ImportedModelInstancedMeshUnit[]): void {
    units.forEach((unit) => {
      unit.listSourceMeshes().forEach((sourceMesh) => {
        const repository = resolveImportedModelTopologyRepository(sourceMesh)
        if (!repository || repository.resolveBodyRecordsByVisibleMesh(sourceMesh).length === 0) {
          return
        }
        const currentRepository = this.repositoryBySourceMesh.get(sourceMesh)
        if (currentRepository === repository) {
          return
        }
        currentRepository?.unregisterRuntimeLookupAnchor(sourceMesh)
        repository.registerRuntimeLookupAnchor(sourceMesh)
        this.repositoryBySourceMesh.set(sourceMesh, repository)
      })
    })
  }

  /** @returns 当前锚点涉及的去重拓扑仓库集合。 */
  public listRepositories(): readonly ImportedModelTopologyRepository[] {
    return Object.freeze([...new Set(this.repositoryBySourceMesh.values())])
  }

  /** 解除当前代次注册的全部 source Mesh 反查锚点。 */
  public clear(): void {
    this.repositoryBySourceMesh.forEach((repository, sourceMesh) => {
      repository.unregisterRuntimeLookupAnchor(sourceMesh)
    })
    this.repositoryBySourceMesh.clear()
  }
}
